import { useState } from "react";
import { motion } from "framer-motion";
import {
  FiUser, FiMail, FiPhone, FiLock, FiCalendar,
  FiCheck, FiArrowRight, FiAlertCircle
} from "react-icons/fi";
import { db } from "./firebase";
import { collection, addDoc } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Navbar from "./Navbar";
import "./Register.css";

// ─── Floating Particles ────────────────────────────────────────
function RegisterParticles() {
  return (
    <div className="register-particles">
      {Array.from({ length: 25 }, (_, i) => (
        <motion.div
          key={i}
          className="register-particle"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: Math.random() * 3 + 1,
            height: Math.random() * 3 + 1,
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.1, 0.4, 0.1],
          }}
          transition={{
            duration: Math.random() * 12 + 8,
            repeat: Infinity,
            delay: Math.random() * 5,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

// ─── Main Register Component ───────────────────────────────────
export default function Register() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  async function submit(event) {
    event.preventDefault();
    setLoading(true);

    const data = new FormData(event.currentTarget);
    const name = data.get("name");
    const email = data.get("email");
    const contact = data.get("contact");
    const gender = data.get("gender");
    const password = data.get("password");
    const dateofbirth = data.get("dateofbirth");

    try {
      await addDoc(collection(db, "Register"), {
        Name: name,
        Email: email,
        Contact: contact,
        Gender: gender,
        Password: password,
        Dateofbirth: dateofbirth,
        PhotoURL: "",
        blocked: false,
      });

      event.target.reset();
      toast.success("Registration Successful! 🎉");
      setTimeout(() => navigate("/Login"), 1500);
    } catch (error) {
      console.error("Registration Error:", error);
      toast.error("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="register-page">
      <RegisterParticles />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar
        theme="dark"
        toastStyle={{
          background: "#0d0d14",
          border: "1px solid rgba(255,255,255,0.1)",
          color: "#f0f0f5",
        }}
      />

      {/* ═══════════ HERO ═══════════ */}
      <section className="register-hero">
        <div className="register-hero-bg" />
        <motion.div
          className="register-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="register-tag">ACCOUNT / REGISTER</span>
          <h1 className="register-hero-title">
            Create <span className="gradient-text">Account</span>
          </h1>
          <p className="register-hero-subtitle">
            Join us today and start exploring all the features.
          </p>
        </motion.div>
      </section>

      {/* ═══════════ MAIN ═══════════ */}
      <main className="register-main">
        <div className="register-container">
          <motion.div
            className="register-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="register-card-header">
              <span className="register-form-tag"><FiUser /> REGISTER</span>
              <h2>Create Your <span className="gradient-text">Account</span></h2>
              <p>Fill in the details below to get started</p>
            </div>

            <form onSubmit={submit} className="register-form">
              <div className="form-row">
                <div className="form-group">
                  <label><FiUser /> Full Name</label>
                  <div className="input-wrapper">
                    <FiUser className="input-icon" />
                    <input
                      type="text"
                      name="name"
                      placeholder="John Doe"
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label><FiMail /> Email</label>
                  <div className="input-wrapper">
                    <FiMail className="input-icon" />
                    <input
                      type="email"
                      name="email"
                      placeholder="john@example.com"
                      className="form-input"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label><FiPhone /> Contact</label>
                  <div className="input-wrapper">
                    <FiPhone className="input-icon" />
                    <input
                      type="tel"
                      name="contact"
                      placeholder="+91 98765-43210"
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Gender</label>
                  <div className="input-wrapper">
                    <select name="gender" className="form-input" required>
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label><FiLock /> Password</label>
                  <div className="input-wrapper">
                    <FiLock className="input-icon" />
                    <input
                      type="password"
                      name="password"
                      placeholder="••••••••"
                      className="form-input"
                      required
                      minLength="6"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label><FiCalendar /> Date of Birth</label>
                  <div className="input-wrapper">
                    <FiCalendar className="input-icon" />
                    <input
                      type="date"
                      name="dateofbirth"
                      className="form-input"
                      required
                    />
                  </div>
                </div>
              </div>

              <motion.button
                type="submit"
                className="register-btn"
                disabled={loading}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                {loading ? (
                  <>
                    <span className="btn-spinner" /> Creating Account...
                  </>
                ) : (
                  <>
                    <FiCheck /> Create Account
                  </>
                )}
              </motion.button>
            </form>

            <div className="divider">
              <span>Already have an account?</span>
            </div>

            <Link to="/Login" className="login-link-btn">
              <FiArrowRight /> Sign In Instead
            </Link>
          </motion.div>
        </div>
      </main>

      <Navbar />
    </div>
  );
}
